import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  ICellRendererParams,
  RowNode,
} from '@ag-grid-community/core'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import {
  EditableCheckboxRenderer,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { RootState, selectIsInternalUser } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  NgGridComponents,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { ISODateFormat, toISOFormat } from '@northfork/basics'
import { InlineEditDisplayOptions } from '@northfork/ng-basics'
import { Subject, of } from 'rxjs'
import { catchError, take, takeUntil, tap } from 'rxjs/operators'
import { Inventory, inventories } from '../../../../model/inventory-type.model'
import { MvtBasesInOut } from '../../../../model/mvt-basis-in-out.model'
import { Basis, BasisType } from '../../../../model/mvt-basis.model'
import {
  PricingRequest,
  PricingResponse,
} from '../../../../model/pricing.model'
import { Security } from '../../../../model/security.model'
import {
  AccountDetails,
  CombinedAccountDetails,
} from '../../../../model/single-transaction/account-details.model'
import {
  ExchangeMappingModel,
  GetBalanceUnitsPayload,
  exchangeField,
} from '../../../../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import {
  normalExchangeTypes,
  statExchangeTypes,
} from '../../../../model/single-transaction/direct-transfer/exchange-type.model'
import { ExchangePositionData } from '../../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../../model/single-transaction/sleeve-simple-accounts.model'
import {
  EXITS,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../../model/transaction-type.model'
import { CopyService } from '../../../../services/copy.service'
import { CreateEditTransactionService } from '../../../../services/create-edit-transaction.service'
import { PasteService } from '../../../../services/single-transaction/paste.service'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  addWarningDetails,
  clearAllInvalidations,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  handleValidation,
  internalAssetIdFilterGridOptions,
  replayDeferredLookups,
} from '../../../../util/common-utils'
import { TransactionConstants } from '../../../../util/constants'
import {
  generateExchangeCreateMapping,
  getCollateralBroker,
} from '../../../../util/direct-transfer-data-mapper-util'

@Component({
  selector: 'hs-exchange-in-out-form',
  templateUrl: './exchange-in-out-form.component.html',
  styleUrls: ['./exchange-in-out-form.component.scss'],
  providers: [PasteService, CopyService],
})
export class ExchangeInOutFormComponent implements AfterViewInit, OnDestroy {
  private _aggregateAccountUnresolved = false
  oldSecurityId: number | undefined
  collateralBroker?: string
  private readonly destroySubject$ = new Subject<void>()
  rowNodeMap = new Map<string, RowNode>()
  gridApi?: AgGridApi
  private isInternalUser = false
  private readonly colDefs: ColDef[] = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  entryDate: string | undefined
  rowData: ExchangeMappingModel[] = generateExchangeCreateMapping()
  private _accountDetails: AccountDetails | undefined
  private _sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  basisControl = new FormControl()

  collateralLabel = 'Source Account Collateral Broker'
  _isSourceCollateral = false

  @Input()
  editMode = false

  /**
   * PVAL-25738: external users must not see the internal Asset ID rows
   * (oldCusip/newCusip) when editing/viewing an existing transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return !this.isInternalUser && this.editMode
  }

  @Input()
  sourceAccountId?: number

  @Input()
  sessionId?: string

  @Input()
  set isSourceCollateral(isSourceCollateral: boolean) {
    this._isSourceCollateral = isSourceCollateral
    if (this.gridApi) this.adjustCollateralBroker()
  }

  @Input() set reconDate(reconDate: Date | undefined) {
    this.entryDate = reconDate?.toString()
    this.setRowValueById(toISOFormat(reconDate) + '', 'entryDate')
  }

  @Input() set accountDetails(accountDetails: AccountDetails | undefined) {
    if (accountDetails) {
      this._accountDetails = accountDetails
      this.setRowValueById(
        accountDetails?.accountId + ' - ' + accountDetails?.accountName,
        'sourceAccount',
      )
    }
  }

  get accountDetails() {
    return this._accountDetails
  }

  @Input() set positionData(
    exchangePositionData: ExchangePositionData | undefined,
  ) {
    if (exchangePositionData) {
      this.setRowValueById(exchangePositionData.oldCusip, 'oldCusip')
      this.setRowValueById(
        exchangePositionData.oldCwanIdentifier ?? '',
        'oldCwanIdentifier',
      )
      this.setRowValueById(exchangePositionData.newCusip, 'newCusip')
      this.setRowValueById(
        exchangePositionData.newCwanIdentifier ?? '',
        'newCwanIdentifier',
      )
      this.setRowValueById(exchangePositionData.units.toString(), 'units')
      if (exchangePositionData.rate !== 0)
        this.setRowValueById(exchangePositionData.rate.toString(), 'rate')
      this.loadPricingInfo(exchangePositionData.oldCusip)
      this.setSecurityDetails(exchangePositionData.newCusip)
      this.collateralBroker = exchangePositionData.collateralBroker
    }
  }

  @Output()
  currentSecurityId = new EventEmitter<number | undefined>()

  @Output()
  editingStarted = new EventEmitter<void>()

  @Output()
  newSecurityId = new EventEmitter<number | undefined>()

  @Output()
  inOutMvtBases = new EventEmitter<MvtBasesInOut>()

  @Output()
  basisSelected = new EventEmitter<Basis[]>()

  @Output()
  dataMap = new EventEmitter<Map<string, RowNode>>()
  @Output()
  editingStopped = new EventEmitter<void>()

  @Output()
  errorDetails = new EventEmitter<unknown>()

  @Output()
  errorHeader = new EventEmitter<string>()

  basis?: Basis[]

  readonly tranType: TransactionTypeKey = 'EXCHANGE_OUT'

  inventories: Inventory[] = inventories
  exchangeTypeValues = normalExchangeTypes
  dateFormat = ISODateFormat

  @Output()
  valueChanged = new EventEmitter<void>()

  @Output()
  sleeveAccountDetails = new EventEmitter<CombinedAccountDetails>()

  @Input() set sleeveSimpleAccount(sleeveAccounts: SleeveSimpleAccounts[]) {
    if (sleeveAccounts?.length > 0) {
      this._sleeveSimpleAccount = sleeveAccounts
      this.setAggAccountDisplayName()
      // Accounts just arrived; if we're waiting on a sub-account choice, open the
      // picker now that it has options to show.
      if (this._aggregateAccountUnresolved && this.gridApi?.api) {
        focusFieldRowEditor(this.gridApi.api, 'sourceAccount')
      }
    }
  }
  get sleeveSimpleAccount() {
    return this._sleeveSimpleAccount
  }

  constructor(
    private readonly createEditTransactionService: CreateEditTransactionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly store: Store<RootState>,
  ) {
    this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .subscribe(isInternalUser => {
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      })
  }

  ngAfterViewInit() {
    this.subscribeFormControls()
    this.dataMap.emit(this.rowNodeMap)
  }

  ngOnDestroy() {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  onBasisUpdate(basis: Basis[]) {
    if (basis.some(b => b.basisType === BasisType.STAT)) {
      this.exchangeTypeValues = statExchangeTypes
    } else {
      this.exchangeTypeValues = normalExchangeTypes
    }
    this.setRowValueById(statExchangeTypes[0].name, 'exchangeInOutType')
    this.basis = basis
  }

  isConversionChecked(value: boolean) {
    if (this.basis?.some(b => b.basisType === BasisType.STAT)) {
      this.setRowValueById(statExchangeTypes[2].name, 'exchangeInOutType')
    }
    const exchangeInOutTypeField = this.rowData.find(
      row => row.id == 'exchangeInOutType',
    )

    if (exchangeInOutTypeField) {
      if (value) {
        exchangeInOutTypeField.isEditable = false
      } else {
        exchangeInOutTypeField.isEditable = true
      }
    }
  }

  setInOutMvtBases(inOutMvtBases?: MvtBasesInOut) {
    this.inOutMvtBases.emit(inOutMvtBases)
  }

  onPricingInfoUpdate(pricingInfo: PricingResponse | null) {
    if (pricingInfo == null) {
      return
    }
    this.currentSecurityId.emit(pricingInfo?.securityId)
    this.setRowValueById(pricingInfo.price?.toString() ?? '', 'transferPrice')
    if (pricingInfo.securityId) {
      this.oldSecurityId = pricingInfo.securityId
      this.getBalanceUnits()
    }

    const oldCusipField = this.rowData.find(row => row.id == 'oldCusip')
    this.errorDetails.emit('')
    // The security lookup is account-scoped; defer it until an account is resolved.
    if (
      this.entryDate &&
      oldCusipField?.value &&
      !this.aggregateAccountUnresolved
    ) {
      // sourceAccountId should be set here; if it isn't, the backend treats the 0 as
      // "no account" and uses the global (clientId=1) security scope, not account 0.
      this.createEditTransactionService
        .fetchSecurityDetailsFromCusip(
          oldCusipField.value.toString(),
          this.entryDate,
          this.sourceAccountId ?? 0,
        )
        .subscribe(
          securityDetails => {
            this.setExchangeTypeForIsDerivative(securityDetails)
          },
          error => {
            this.setRowValueById('', 'oldCusip')
            this.errorDetails.emit(error.error)
            this.errorHeader.emit(TransactionConstants.ERROR_LOADING_SECURITY)
          },
        )
    }
    this.valueChanged.emit()
  }

  getBalanceUnits() {
    if (
      this.editMode ||
      this.oldSecurityId == null ||
      this.entryDate == null ||
      this.sourceAccountId == null
    )
      return

    const inventoryType =
      this.gridApi?.api.getRowNode('inventoryType')?.data.value
    const collateralBroker =
      this.gridApi?.api.getRowNode('collateralBroker')?.data.value

    const getBalanceUnitsPayload: GetBalanceUnitsPayload = {
      date: this.entryDate,
      securityId: this.oldSecurityId,
      accountId: this.sourceAccountId,
      ...(inventoryType && { inventoryType }),
      ...(collateralBroker && { collateralBroker }),
    }

    this.createEditTransactionService
      .getBalanceUnits(getBalanceUnitsPayload)
      .pipe(
        take(1),
        tap(response => {
          this.setRowValueById(
            response.balanceUnits.toString() ?? '',
            'balanceUnits',
          )
          this.setRowValueById(
            response && response.balanceUnits < 0 ? 'SHORT' : 'LONG',
            'inventoryType',
          )
          this.valueChanged.emit()
        }),
        catchError(error => {
          this.errorDetails.emit(error.error)
          this.errorHeader.emit('Error fetching balance units')
          return of(null)
        }),
      )
      .subscribe()
  }

  setErrorDetails(errorDetails: unknown) {
    this.errorDetails.emit(errorDetails)
  }

  setErrorHeader(errorHeader: string | undefined) {
    this.errorHeader.emit(errorHeader)
  }
  setSecurityDetails(newCusip: string | undefined) {
    // The security lookup is account-scoped; defer it until an account is resolved.
    if (this.aggregateAccountUnresolved) return
    this.errorDetails.emit('')
    if (this.entryDate && newCusip) {
      // sourceAccountId should be set here; if it isn't, the backend treats the 0 as
      // "no account" and uses the global (clientId=1) security scope, not account 0.
      this.createEditTransactionService
        .fetchSecurityDetailsFromCusip(
          newCusip,
          this.entryDate,
          this.sourceAccountId ?? 0,
        )
        .subscribe(
          securityDetails => {
            this.newSecurityId.emit(securityDetails?.securityMasterData.id)
            this.setExchangeTypeForIsDerivative(securityDetails)
          },
          error => {
            this.setRowValueById('', 'newCusip')
            this.errorDetails.emit(error.error)
            this.errorHeader.emit(TransactionConstants.ERROR_LOADING_SECURITY)
          },
        )
    }
  }

  setExchangeTypeForIsDerivative(security: Security | undefined) {
    if (security?.securityTypeData?.isDerivative) {
      this.setRowValueById(statExchangeTypes[1].name, 'exchangeInOutType')
    }
  }

  subscribeFormControls() {
    this.basisControl.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(basis => {
        this.basisSelected.emit(basis)
      })
  }

  setRowValueById(value: string, id: string) {
    const rowField = this.rowData.find(row => row.id == id)
    if (rowField) {
      rowField.value = value
      this.gridApi?.api.refreshCells()
    }
  }

  get colDef() {
    return this.colDefs
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: exchangeField('isEditable'),
        hide: true,
      },
      {
        field: exchangeField('errorMessage'),
        hide: true,
      },
      {
        field: exchangeField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
      },
      {
        field: exchangeField('field'),
        headerName: 'Field',
        maxWidth: 200,
      },
      {
        field: exchangeField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRenderer: undefined,
        cellRendererSelector: params => {
          const isCheckboxRow = this.isCheckBoxCell(params)
          const component = isCheckboxRow
            ? EditableCheckboxRenderer
            : NgGridComponents.inlineEditDisplay
          return {
            component,
            params,
          }
        },
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  getTypeSign(): number {
    const tranSign = EXITS.includes(TransactionTypeEnum[this.tranType]) ? -1 : 1
    const inventorySign =
      this.rowNodeMap.get('inventoryType')?.data.value == 'SHORT' ? -1 : 1
    return tranSign * inventorySign
  }

  isCheckBoxCell(params: ICellRendererParams): boolean {
    if (params.data.id == 'isConversion') {
      return true
    }
    return false
  }

  // When the parent has an unresolved aggregate account, defer validation and flag the
  // source-account picker red until a concrete sub-account is chosen.
  @Input() set aggregateAccountUnresolved(value: boolean) {
    this._aggregateAccountUnresolved = value
    if (value) {
      // Flag the account row red until a concrete sub-account is chosen.
      const accountRow = this.rowData.find(row => row.id == 'sourceAccount')
      if (accountRow) {
        accountRow.validityStatus = 'invalid'
        accountRow.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
    }
    this.gridApi?.api.refreshCells({ force: true })
    // Only open the picker once its options have loaded; otherwise the select
    // opens stuck on its (static) loading state.
    if (value && this.sleeveSimpleAccount.length > 0 && this.gridApi?.api) {
      focusFieldRowEditor(this.gridApi.api, 'sourceAccount')
    }
  }
  get aggregateAccountUnresolved(): boolean {
    return this._aggregateAccountUnresolved
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'entryDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'sourceAccount':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'displayLabel',
            bindLabel: 'displayLabel',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      case 'exchangeInOutType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.exchangeTypeValues,
            bindValue: 'name',
            bindLabel: 'name',
          },
        }
      case 'newCusip':
      case 'oldCusip':
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
      case 'marketPrice':
      case 'units':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'rate':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
          validators: [Validators.required],
        }
      case 'inventoryType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: inventories,
            bindValue: 'id',
            bindLabel: 'id',
          },
        }
      case 'isConversion':
        return {
          dataType: InlineEditDataType.Checkbox,
        }
      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => {
        this.gridApi = event
        resizeColumns(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.adjustCollateralBroker()
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.editingStarted.emit()
      },
      onCellEditingStopped: () => {
        this.editingStopped.emit()
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      getRowNodeId: ({ id }: ExchangeMappingModel) => id,
      columnDefs: this.colDefs,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFirstDataRendered: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        if (
          event.column.getColId() === 'value' &&
          event.data.id == 'balanceUnits' &&
          event.data.value != '--'
        ) {
          const units = Math.abs(event.data.value) * this.getTypeSign()
          this.setRowValueById(Math.abs(units).toString(), 'units')
          this.valueChanged.emit()
        } else {
          const element = event.event?.target as HTMLElement
          if (element.className == 'ag-fill-handle') {
            fillHandleDoubleClickHandlerSingleDialog(event.api)
          } else {
            event.api.startEditingCell({
              rowIndex: event.rowIndex ?? 0,
              colKey: event.column.getColId(),
            })
          }
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  async onColumnValueChanged(event: CellValueChangedEvent) {
    const currentInventoryType = this.rowData?.find(
      row => row.id == 'inventoryType',
    )?.value
    const currentUnit = this.rowData?.find(row => row.id == 'units')?.value
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }
    if (
      event.data['id'] != 'sourceAccount' &&
      this.aggregateAccountUnresolved
    ) {
      // No account yet: flag the edited field instead of running account-scoped lookups.
      addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
      event.api.refreshCells({ force: true, rowNodes: [event.node] })
      this.dataMap.emit(this.rowNodeMap)
      this.valueChanged.emit()
      return
    }
    if (event.data['id'] == 'oldCusip') {
      this.loadPricingInfo()
    } else if (event.data['id'] == 'newCusip') {
      this.setSecurityDetails(event.data['value'])
    } else if (event.data['id'] == 'entryDate') {
      this.entryDate = event.data['value']
      this.loadPricingInfo()
    } else if (event.data['id'] == 'isConversion') {
      this.isConversionChecked(event.data['value'])
    } else if (event.data['id'] == 'sourceAccount') {
      await this.loadAggAccountDetails(event.data['value'])
      this.loadPricingInfo()
      this.getBalanceUnits()
    } else if (event.data['id'] == 'units' && currentInventoryType != 'ISSUE') {
      this.setRowValueById(
        Math.abs(Number(event.data['value'])).toString(),
        'units',
      )
    } else if (event.data['id'] == 'inventoryType') {
      this.getBalanceUnits()
      if (currentInventoryType != 'ISSUE') {
        this.setRowValueById(Math.abs(Number(currentUnit)).toString(), 'units')
      }
    } else if (event.data['id'] == 'collateralBroker') {
      this.getBalanceUnits()
    }
    this.dataMap.emit(this.rowNodeMap)
    this.valueChanged.emit()
  }

  loadPricingInfo(oldCusipPassed?: string) {
    const oldCusip: string | undefined =
      oldCusipPassed ?? this.gridApi?.api.getRowNode('oldCusip')?.data.value
    const inventoryType =
      this.gridApi?.api.getRowNode('inventoryType')?.data.value
    const collateralBroker =
      this.gridApi?.api.getRowNode('collateralBroker')?.data.value
    if (
      this.sessionId == null ||
      this.entryDate == null ||
      this.entryDate === '' ||
      this.sourceAccountId == null ||
      !oldCusip ||
      oldCusip === ''
    ) {
      return
    }
    const pricingRequest: PricingRequest = {
      entryDate: this.entryDate,
      accountId: this.sourceAccountId,
      cusip: oldCusip,
      ...(inventoryType && { inventoryType }),
      ...(collateralBroker && { collateralBroker }),
    }

    this.createEditTransactionService
      .loadPricingInformation(pricingRequest)
      .pipe(
        take(1),
        tap(pricingInfo => this.onPricingInfoUpdate(pricingInfo)),
        catchError(error => {
          this.errorDetails.emit(error.error)
          this.errorHeader.emit('Error fetching pricing information')
          return of(null)
        }),
      )
      .subscribe()
  }

  handleValidation(
    rowNode: RowNode,
    validationState: string,
    validationMessage: string,
  ) {
    if (this.gridApi)
      handleValidation(rowNode, validationState, validationMessage)
  }

  async loadAggAccountDetails(accountDisplayName: string) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == accountDisplayName,
    )
    if (account) {
      const accountDetailsPromise = this.createEditTransactionService
        .loadAccountDetails(account.id)
        .toPromise()
      const isCollateralBrokerPromise = this.createEditTransactionService
        .isCollateralBroker([account.id])
        .toPromise()
      return Promise.all([
        accountDetailsPromise,
        isCollateralBrokerPromise,
      ]).then(([accountDetails, isCollateralBrokerMap]) => {
        this.accountDetails = accountDetails
        this.sourceAccountId = accountDetails.accountId
        this._isSourceCollateral = isCollateralBrokerMap[account.id]
        // A concrete account is now resolved; clear the account-unresolved marks. The parent
        // owns the flag and re-validation once it picks up the resolved account details.
        this._aggregateAccountUnresolved = false
        clearAllInvalidations(this.gridApi?.api)
        // Replay lookups deferred while the account was unresolved (e.g. cusips entered first)
        // so the security/pricing-derived columns pull now that we have an account.
        replayDeferredLookups(this.gridApi?.api, [
          { rowId: 'oldCusip', run: cusip => this.loadPricingInfo(cusip) },
          { rowId: 'newCusip', run: cusip => this.setSecurityDetails(cusip) },
        ])
        this.sleeveAccountDetails.emit({
          accountDetails: this.accountDetails,
          isCollateral: this._isSourceCollateral,
        })
        if (this.gridApi) this.adjustCollateralBroker()
      })
    }
  }

  setAggAccountDisplayName() {
    const accountRowField = this.rowData?.find(row => row.id == 'sourceAccount')
    if (accountRowField) {
      accountRowField.isEditable = true
      accountRowField.value = ''
    }
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  adjustCollateralBroker() {
    const collateralNode = this.gridApi?.api.getRowNode('collateralBroker')
    if (this._isSourceCollateral) {
      if (!collateralNode) {
        const collateralNodeData = getCollateralBroker(
          this.collateralLabel,
          this.collateralBroker,
        ) as ExchangeMappingModel
        this.rowData.push(collateralNodeData)
        this.gridApi?.api.applyTransaction({
          add: [collateralNodeData],
        })
        this.valueChanged.emit()
      }
    } else {
      if (collateralNode) {
        this.gridApi?.api.applyTransaction({
          remove: [collateralNode],
        })
        this.valueChanged.emit()
      }
    }
  }
}
